import { useContext, useState } from 'react'
import { Box, MenuItem, Modal, Button, Select, TextField, Tooltip, IconButton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TableComponent from '../../../shared/Table'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'
import { LocationTemplateConfigs } from '../../../../utils/constants'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import { ILocationTemplateWithRegions } from '../../../../types/spectrum-manager.type'
import { ILocationTemplate } from '../../../../types/exercise.type'
import { RequestContext } from '../../../../contexts/request.context'
import {
  createLocationTemplate,
  deleteLocationTemplate,
  updateLocationTemplate,
} from '../../../../services/spectrum-manager.service'
import { useToast } from '../../../../contexts/toast.context'
import { AppConfigContext } from '../../../../contexts/app-config.context'
import { usePermission } from '../../../../hooks/usePermission'

import styles from './styles.module.scss'

const requiredFields: (keyof ILocationTemplateWithRegions)[] = [
  'template_name',
  'transmitter_location',
  'state_region_type_id',
  'longitude_deg',
  'longitude_min',
  'longitude_sec',
  'latitude_deg',
  'latitude_min',
  'latitude_sec',
]

const isFieldMissing = (template: ILocationTemplateWithRegions, field: keyof ILocationTemplateWithRegions) => {
  const value = template[field]
  if (typeof value === 'string') return !value.trim()
  if (field === 'state_region_type_id') return !value
  return value == null
}

export const LocationTemplates = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [locationTemplate, setLocationTemplate] = useState<ILocationTemplateWithRegions>(
    {} as ILocationTemplateWithRegions,
  )

  const { showToast } = useToast()
  const { locationTemplatesWithRegions, setLocationTemplatesWithRegions } = useContext(SpectrumManagerContext)
  const { isLightMode } = useContext(AppConfigContext)
  const { stateRegions, locationTemplates, setLocationTemplates } = useContext(RequestContext)

  const handleFieldChange = (key: string, value: string | number) => {
    setLocationTemplate((prev) => ({ ...prev, [key]: value }))
  }

  const handleCreate = () => {
    setLocationTemplate({} as ILocationTemplateWithRegions)
    setIsEditing(false)
    setIsModalOpen(true)
  }

  const handleEdit = (template: ILocationTemplateWithRegions) => {
    setLocationTemplate(template)
    setIsEditing(true)
    setIsModalOpen(true)
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setIsEditing(false)
    setSubmitted(false)
  }

  const handleSubmit = async () => {
    const hasErrors = requiredFields.some((field) => isFieldMissing(locationTemplate, field))
    if (hasErrors) {
      setSubmitted(true)
      return
    }

    if (isEditing) {
      const body = await updateLocationTemplate(locationTemplate.id, locationTemplate)

      if (body.data) {
        const updated = body.data
        setLocationTemplatesWithRegions(locationTemplatesWithRegions.map((t) => (t.id === updated.id ? updated : t)))
        setLocationTemplates(
          locationTemplates.map((t) => (t.id === updated.id ? ({ ...t, ...updated } as ILocationTemplate) : t)),
        )
        showToast('success', 'Template updated successfully')
        handleClose()
      }
      // On failure the axios interceptor already shows an error toast; leave the modal open.
    } else {
      const body = await createLocationTemplate(locationTemplate)

      if (body.data) {
        const created = body.data
        setLocationTemplatesWithRegions([created, ...locationTemplatesWithRegions])
        setLocationTemplates([{ ...created, elevation: null } as ILocationTemplate, ...locationTemplates])
        showToast('success', 'Template created successfully')
        handleClose()
      }
      // On failure the axios interceptor already shows an error toast; leave the modal open.
    }
  }

  const handleRemoveLocation = async (id: number) => {
    const body = await deleteLocationTemplate(id)

    if (body.success) {
      setLocationTemplatesWithRegions(locationTemplatesWithRegions.filter((t) => t.id !== id))
      setLocationTemplates(locationTemplates.filter((t) => t.id !== id))
      showToast('success', 'Template removed successfully')
    } else {
      showToast('error', 'Cannot delete this template because it is currently in use by one or more requests', {
        autoClose: 4000,
      })
    }
  }

  const regionOptions = stateRegions.map((region) => ({ value: region.id, label: region.name }))
  const selectedRegion = regionOptions.find((opt) => opt.value === locationTemplate?.state_region_type_id)

  const err = (field: keyof ILocationTemplateWithRegions) => submitted && isFieldMissing(locationTemplate, field)

  return (
    <Box className={styles.locationTemplate}>
      <TableComponent
        isLightMode={isLightMode}
        handleCreate={handleCreate}
        handleEdit={handleEdit}
        data={locationTemplatesWithRegions}
        configs={LocationTemplateConfigs}
        handleRefetch={() => {}}
        handleDelete={handleRemoveLocation}
      />
      <Modal className={styles.modal} open={isModalOpen} onClose={handleClose}>
        <Box className={styles.modalContent}>
          <Box className={styles.header}>
            <Typography data-testid="title" className={styles.title}>
              {isEditing ? 'Edit Location' : 'Add Location'}
            </Typography>
            <IconButton data-testid="closeButton" onClick={handleClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Box className={styles.content}>
            <Box className={styles.addTemplate}>
              <Box className={styles.subContainer}>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Template Name{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Template Name"
                    className={styles.field}
                    value={locationTemplate?.template_name || ''}
                    onChange={(e) => handleFieldChange('template_name', e.target.value)}
                    error={err('template_name')}
                    helperText={err('template_name') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Transmitter Location{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Transmitter Location"
                    className={styles.field}
                    value={locationTemplate?.transmitter_location || ''}
                    onChange={(e) => handleFieldChange('transmitter_location', e.target.value)}
                    error={err('transmitter_location')}
                    helperText={err('transmitter_location') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Region{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <SelectWithSearch
                      label="Regions"
                      width={250}
                      options={regionOptions}
                      value={selectedRegion}
                      handleChange={(value) => handleFieldChange('state_region_type_id', Number(value))}
                    />
                    {err('state_region_type_id') && <Typography className={styles.fieldError}>Required</Typography>}
                  </Box>
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>Afc:</Typography>
                  <TextField
                    label="Afc"
                    className={styles.field}
                    value={locationTemplate?.afc || ''}
                    onChange={(e) => handleFieldChange('afc', e.target.value)}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>Afc code:</Typography>
                  <TextField
                    label="Afc code"
                    className={styles.field}
                    value={locationTemplate?.afc_code || ''}
                    onChange={(e) => handleFieldChange('afc_code', e.target.value)}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>Afc coord note:</Typography>
                  <TextField
                    label="Afc coord note"
                    className={styles.field}
                    value={locationTemplate?.afc_coord_note || ''}
                    onChange={(e) => handleFieldChange('afc_coord_note', e.target.value)}
                  />
                </Box>
              </Box>
              <Box className={styles.subContainer}>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Longitude Deg{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Longitude Deg"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.longitude_deg ?? ''}
                    onChange={(e) => handleFieldChange('longitude_deg', Number(e.target.value))}
                    error={err('longitude_deg')}
                    helperText={err('longitude_deg') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Longitude Min{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Longitude Min"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.longitude_min ?? ''}
                    onChange={(e) => handleFieldChange('longitude_min', Number(e.target.value))}
                    error={err('longitude_min')}
                    helperText={err('longitude_min') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Longitude Sec{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Longitude Sec"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.longitude_sec ?? ''}
                    onChange={(e) => handleFieldChange('longitude_sec', Number(e.target.value))}
                    error={err('longitude_sec')}
                    helperText={err('longitude_sec') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Latitude Deg{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Latitude Deg"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.latitude_deg ?? ''}
                    onChange={(e) => handleFieldChange('latitude_deg', Number(e.target.value))}
                    error={err('latitude_deg')}
                    helperText={err('latitude_deg') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Latitude Min{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Latitude Min"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.latitude_min ?? ''}
                    onChange={(e) => handleFieldChange('latitude_min', Number(e.target.value))}
                    error={err('latitude_min')}
                    helperText={err('latitude_min') && 'Required'}
                  />
                </Box>
                <Box className={styles.fieldContainer}>
                  <Typography className={styles.label}>
                    Latitude Sec{' '}
                    <Typography component="span" className={styles.asterisk}>
                      *
                    </Typography>
                    :
                  </Typography>
                  <TextField
                    label="Latitude Sec"
                    type="number"
                    className={styles.field}
                    value={locationTemplate?.latitude_sec ?? ''}
                    onChange={(e) => handleFieldChange('latitude_sec', Number(e.target.value))}
                    error={err('latitude_sec')}
                    helperText={err('latitude_sec') && 'Required'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={styles.actions}>
            <Button id="confirmButton" data-testid="confirmButton" className={styles.removeBtn} onClick={handleSubmit}>
              {isEditing ? 'Save' : 'Create'}
            </Button>
            <Button className={styles.cancelBtn} color="inherit" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}
